import React from "react";
import { Panel, Title, Text, Pill, Table, Field, Skeleton, Icon } from "components/commons";
import { Collapse, Divider } from "antd";
import lang from "translations";
import { PillType } from "enums";
import { v4 as uuidv4 } from "uuid";

const reconciliationPillType = (status) => {
  if (status === "RECONCILED") return PillType.Green;
  if (status === "UNRECONCILED") return PillType.Red;
  return PillType.Yellow; // PENDING
};

const billStatusPillType = (status) => {
  switch (status) {
    case "SETTLED":
      return PillType.Green;
    case "VOID":
      return PillType.Red;
    default:
      return PillType.Yellow; // OPEN
  }
};

const lifecycleStatusPillType = (status) => {
  switch (status) {
    case "SERVED":
    case "COMPLETED":
    case "PAID":
      return PillType.Green;
    case "VOIDED":
      return PillType.Red;
    default:
      return PillType.Yellow;
  }
};

const splitColumns = [
  { text: lang.splitNumber, key: "splitNumber" },
  { text: lang.guest, key: "guestRef" },
  { text: lang.splitType, key: "splitType" },
  { text: lang.amount, key: "splitAmount", align: "right" },
  { text: lang.paidAmount, key: "paidAmount", align: "right" },
  { text: lang.discountAmount, key: "discountAmount", align: "right" },
  { text: lang.voucherAmount, key: "voucherAmount", align: "right" },
  { text: lang.reconciliationStatus, key: "reconciliationStatus", custom: true },
];

const itemColumns = [
  { text: lang.productName, key: "productName" },
  { text: lang.quantity, key: "quantity", align: "right" },
  { text: lang.price, key: "price", align: "right" },
  { text: lang.taxAmount, key: "taxAmount", align: "right" },
  { text: lang.serviceCharge, key: "serviceChargeAmount", align: "right" },
  { text: lang.notes, key: "notes" },
  { text: lang.status, key: "status", custom: true },
];

const splitItemColumns = [
  { text: lang.productName, key: "productName" },
  { text: lang.quantity, key: "quantity", align: "right" },
  { text: lang.amount, key: "amount", align: "right" },
];

const paymentColumns = [
  { text: lang.method, key: "method" },
  { text: lang.amount, key: "amount", align: "right" },
  { text: lang.reference, key: "referenceId" },
  { text: lang.status, key: "status" },
  { text: lang.date, key: "paidAt" },
  { text: lang.selected, key: "isSelected", custom: true },
];

const StatLabel = ({ children }) => (
  <Text uppercase strong color="text-gray" size="text-xs">
    {children}
  </Text>
);

const SectionHeading = ({ icon, children }) => (
  <div className="flex items-center gap-2 mb-sm">
    <Icon name={icon} className="text-pelorous" fontSize="16px" />
    <Text uppercase strong color="text-gray" size="text-xs">
      {children}
    </Text>
  </div>
);

// Shows the bill/split/payment-audit-trail/order/reservation data correlated to this
// transaction (GET /transaction/{id}/pos-sync/detail) -- separate API call from the main
// transaction fetch, rendered only once a bill has actually synced. Every paymentInfo[]
// attempt per split is shown, not just the reconciled one (per-split collapse), since that
// audit trail is the whole point of bill_split_payment -- see
// ms-inventory-integration-reference.md §9/§14.
const TransactionPosSyncPanel = ({ bill, loading }) => {
  if (loading) {
    return (
      <Panel className="mt-md">
        <Title className="mb-md">{lang.posSyncDetails}</Title>
        <Skeleton />
      </Panel>
    );
  }

  if (!bill) {
    return null;
  }

  const splitRows = bill.splits.map((split) => ({
    ...split,
    guestRef: split.guestRef || "-",
    reconciliationStatus: (
      <Pill size="text-xs" type={reconciliationPillType(split.reconciliationStatus)}>
        {split.reconciliationStatus}
      </Pill>
    ),
  }));

  return (
    <Panel className="mt-md">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-md">
        <div className="flex items-center gap-2">
          <Icon name="receipt" className="text-pelorous" fontSize="20px" />
          <Title>{lang.posSyncDetails}</Title>
        </div>
        <Pill type={billStatusPillType(bill.status)}>{bill.status}</Pill>
      </div>

      <div className="bg-gray-lightest rounded p-md mb-md">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <StatLabel>{lang.billNumber}</StatLabel>
            <Text className="mt-xs">{lang.bill} #{bill.billHeaderId}</Text>
          </div>
          {bill.billType && (
            <div>
              <StatLabel>{lang.billType}</StatLabel>
              <Text className="mt-xs">{bill.billType}</Text>
            </div>
          )}
          <div>
            <StatLabel>{lang.openedAt}</StatLabel>
            <Text className="mt-xs">{bill.openedAt}</Text>
          </div>
          <div>
            <StatLabel>{lang.settledAt}</StatLabel>
            <Text className="mt-xs">{bill.settledAt}</Text>
          </div>
          <div className="text-right">
            <StatLabel>{lang.paidAmount}</StatLabel>
            <Title lg className="mt-xs">
              {bill.paidAmount}
            </Title>
          </div>
        </div>
        <Divider className="my-md" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label={lang.grossAmount}>
            <Text>{bill.grossAmount}</Text>
          </Field>
          <Field label={lang.discountAmount}>
            <Text>{bill.discountAmount}</Text>
          </Field>
          <Field label={lang.voucherAmount}>
            <Text>{bill.voucherAmount}</Text>
          </Field>
          <Field label={lang.taxAmount}>
            <Text>{bill.taxAmount}</Text>
          </Field>
          <Field label={lang.netAmount}>
            <Text>{bill.netAmount}</Text>
          </Field>
        </div>
      </div>

      {bill.reservation && (
        <div className="border border-gray-lightest rounded p-md mb-md">
          <SectionHeading icon="guest">{lang.reservation}</SectionHeading>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field label={lang.reservationId}>
              <Text>#{bill.reservation.posReservationId}</Text>
            </Field>
            <Field label={lang.status}>
              <Pill size="text-xs" type={lifecycleStatusPillType(bill.reservation.status)}>
                {bill.reservation.status || "-"}
              </Pill>
            </Field>
            <Field label={lang.resourceId}>
              <Text>{bill.reservation.resourceId ?? "-"}</Text>
            </Field>
            <Field label={lang.guestCount}>
              <Text>{bill.reservation.guestCount ?? "-"}</Text>
            </Field>
            {bill.reservation.closeReason && (
              <Field label={lang.closeReason}>
                <Text>{bill.reservation.closeReason}</Text>
              </Field>
            )}
          </div>
          {bill.reservation.guests.length > 0 && (
            <div className="mt-md flex flex-wrap gap-2">
              {bill.reservation.guests.map((guest) => (
                <Pill
                  key={uuidv4()}
                  size="text-xs"
                  type={guest.isPrimary ? PillType.Blue : PillType.Gray}
                >
                  {guest.isPrimary && <Icon name="star" fontSize="9px" className="mr-xs" />}
                  {guest.name}
                </Pill>
              ))}
            </div>
          )}
        </div>
      )}

      {bill.splits.length > 0 && (
        <div className="mb-md">
          <SectionHeading icon="divide">{lang.splits}</SectionHeading>
          <div className="overflow-x-auto overflow-hidden">
            <Table data={splitRows} columns={splitColumns} minWidth="900px" />
          </div>
          <Collapse ghost className="mt-sm">
            {bill.splits.map((split) => (
              <Collapse.Panel
                key={split.billSplitId}
                header={
                  <Text size="text-sm">
                    {lang.splitNumber} {split.splitNumber} — {lang.productName} ({split.items.length}), {lang.paymentAttempts} ({split.payments.length})
                  </Text>
                }
              >
                {split.items.length > 0 && (
                  <div className="overflow-x-auto overflow-hidden mb-sm">
                    <Table data={split.items} columns={splitItemColumns} minWidth="500px" />
                  </div>
                )}
                <div className="overflow-x-auto overflow-hidden">
                  <Table
                    data={split.payments.map((payment) => ({
                      ...payment,
                      isSelected: payment.isSelected ? (
                        <Pill size="text-xs" type={PillType.Green}>
                          <Icon name="check-only" fontSize="9px" className="mr-xs" />
                          {lang.selected}
                        </Pill>
                      ) : (
                        <Text color="text-gray-400">-</Text>
                      ),
                    }))}
                    columns={paymentColumns}
                    minWidth="700px"
                  />
                </div>
              </Collapse.Panel>
            ))}
          </Collapse>
        </div>
      )}

      {bill.orders.length > 0 && (
        <div>
          <SectionHeading icon="burger-cup">{lang.orders}</SectionHeading>
          <div className="grid grid-cols-1 gap-3">
            {bill.orders.map((order, index) => (
              <Panel key={order.orderId || order.orderLocalId} bordered mb="0">
                <div className="flex items-center gap-2 mb-sm">
                  <Text strong>{lang.order} {index + 1}</Text>
                  <Pill size="text-xs" type={lifecycleStatusPillType(order.status)}>
                    {order.status}
                  </Pill>
                </div>
                <div className="overflow-x-auto overflow-hidden">
                  <Table
                    data={order.items.map((item) => ({
                      ...item,
                      status: (
                        <Pill size="text-xs" type={lifecycleStatusPillType(item.status)}>
                          {item.status}
                        </Pill>
                      ),
                    }))}
                    columns={itemColumns}
                    minWidth="700px"
                  />
                </div>
              </Panel>
            ))}
          </div>
        </div>
      )}
    </Panel>
  );
};

export default TransactionPosSyncPanel;
